import { dictionaryApi } from './dictionaryClient';
import {
  TINGANTA_RUP_KARTARI_FIELD_KEYS,
  TINGANTA_RUP_KARMANI_FIELD_KEYS,
  TINGANTA_RUP_PARASMAI_FIELD_KEYS,
} from '../constants/tingantaRupFieldKeys';

const TS_META = new Set([
  'id',
  'tingantaId',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
]);

export type TingantaFixedRupRow = {
  id: number;
  tingantaId: number;
} & Record<string, string | null | undefined>;

function numOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Backend often returns one row as an object (not `[row]`) for `?tingantaId=` filters. */
function normalizeList(data: unknown): TingantaFixedRupRow[] {
  if (Array.isArray(data)) return data as TingantaFixedRupRow[];
  if (data && typeof data === 'object') {
    const wrap = data as { data?: unknown };
    if (Array.isArray(wrap.data)) {
      return wrap.data as TingantaFixedRupRow[];
    }
    if (wrap.data != null && typeof wrap.data === 'object' && !Array.isArray(wrap.data)) {
      const inner = wrap.data as Record<string, unknown>;
      if (numOrUndef(inner.id) != null) {
        return [wrap.data as TingantaFixedRupRow];
      }
    }
    const o = data as Record<string, unknown>;
    if (numOrUndef(o.id) != null) {
      return [data as TingantaFixedRupRow];
    }
  }
  return [];
}

function normStr(v: unknown): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t === '' ? null : t;
}

/**
 * Backend-specific property names on `Tinganta` that differ from generated keys
 * (e.g. TypeORM inverse is `rup` for RupKartari, not `rupKartari`).
 */
const TINGANTA_DETAIL_EMBED_EXTRA: Record<string, string[]> = {
  '/tinganta-rup-kartari': ['rup'],
};

/**
 * Candidate JSON keys on `GET /tinganta/:id` for a nested one-to-one relation,
 * e.g. `/tinganta-rup-karmani` → tingantaRupKarmani, rupKarmani, tinganta_rup_karmani.
 */
export function tingantaRelationEmbedKeys(basePath: string): string[] {
  const path = basePath.startsWith('/') ? basePath : `/${basePath}`;
  const slug = path.replace(/^\//, '').split('/').pop() ?? '';
  const segs = slug.split('-').filter(Boolean);
  if (segs.length === 0) return [];
  const pascalTail = segs.slice(1).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
  const full = segs[0] + pascalTail;
  const short =
    segs[0] === 'tinganta' && segs.length > 1
      ? segs[1] + segs.slice(2).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')
      : '';
  const snake = segs.join('_');
  const pluralFull = `${full}s`;
  const pluralShort = short ? `${short}s` : '';
  const extras = TINGANTA_DETAIL_EMBED_EXTRA[path] ?? [];
  return Array.from(
    new Set(
      [...extras, full, short, snake, pluralFull, pluralShort].filter(Boolean)
    )
  );
}

function coalesceOneToOneRow(raw: unknown, tingantaId: number): TingantaFixedRupRow | null {
  if (raw == null) return null;
  let obj: unknown = raw;
  if (Array.isArray(raw)) {
    obj =
      raw.find((x) => {
        if (!x || typeof x !== 'object') return false;
        const id = numOrUndef((x as Record<string, unknown>).id);
        return id != null;
      }) ?? raw[0];
  }
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  const rowId = numOrUndef(o.id);
  if (rowId == null) return null;
  const tid = numOrUndef(o.tingantaId ?? o.tinganta_id);
  if (tid != null && tid !== tingantaId) return null;
  return { ...o, id: rowId, tingantaId: tid ?? tingantaId } as TingantaFixedRupRow;
}

/** Read nested rūpa row from tiṅganta detail payload. */
export function pickEmbeddedFixedRup(
  detail: Record<string, unknown> | null | undefined,
  candidateKeys: string[],
  tingantaId: number
): TingantaFixedRupRow | null {
  if (!detail) return null;
  for (const key of candidateKeys) {
    const raw = detail[key];
    const row = coalesceOneToOneRow(raw, tingantaId);
    if (row) return row;
  }
  return null;
}

export function pickEmbeddedKrutRow(
  detail: Record<string, unknown> | null | undefined,
  candidateKeys: string[],
  tingantaId: number
): TingantaKrutRow | null {
  const r = pickEmbeddedFixedRup(detail, candidateKeys, tingantaId);
  return r as TingantaKrutRow | null;
}

export type FixedRupResource = {
  basePath: string;
  scalarKeys: readonly string[];
  list: (tingantaId?: number) => Promise<TingantaFixedRupRow[]>;
  get: (id: number) => Promise<TingantaFixedRupRow>;
  create: (body: { tingantaId: number } & Record<string, unknown>) => Promise<TingantaFixedRupRow>;
  update: (
    id: number,
    patch: Partial<Record<string, string | null>>
  ) => Promise<TingantaFixedRupRow>;
  remove: (id: number) => Promise<void>;
};

export function createFixedRupResource(
  basePath: string,
  scalarKeys: readonly string[]
): FixedRupResource {
  const path = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return {
    basePath: path,
    scalarKeys,
    async list(tingantaId?: number): Promise<TingantaFixedRupRow[]> {
      const res = await dictionaryApi.get<unknown>(path, {
        params: tingantaId != null ? { tingantaId } : undefined,
      });
      return normalizeList(res.data);
    },
    async get(id: number): Promise<TingantaFixedRupRow> {
      const res = await dictionaryApi.get<TingantaFixedRupRow>(`${path}/${id}`);
      return res.data;
    },
    async create(body: { tingantaId: number } & Record<string, unknown>): Promise<TingantaFixedRupRow> {
      const payload: Record<string, unknown> = { tingantaId: body.tingantaId };
      for (const k of scalarKeys) {
        const v = body[k];
        const n = normStr(v);
        if (n != null) payload[k] = n;
      }
      const res = await dictionaryApi.post<TingantaFixedRupRow>(path, payload);
      return res.data;
    },
    async update(
      id: number,
      patch: Partial<Record<string, string | null>>
    ): Promise<TingantaFixedRupRow> {
      const res = await dictionaryApi.patch<TingantaFixedRupRow>(`${path}/${id}`, patch);
      return res.data;
    },
    async remove(id: number): Promise<void> {
      await dictionaryApi.delete(`${path}/${id}`);
    },
  };
}

export function buildFixedRupPatch(
  prev: TingantaFixedRupRow,
  next: Record<string, unknown>,
  scalarKeys: readonly string[]
): Partial<Record<string, string | null>> {
  const patch: Partial<Record<string, string | null>> = {};
  for (const k of scalarKeys) {
    const p = normStr(prev[k]);
    const n = normStr(next[k]);
    if (p !== n) patch[k as string] = n;
  }
  return patch;
}

export function rowToFormValues(
  row: TingantaFixedRupRow | null,
  scalarKeys: readonly string[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of scalarKeys) {
    const v = row?.[k];
    out[k] = v == null ? '' : String(v);
  }
  return out;
}

/** Fixed-schema resources (one row per tinganta). */
export const tingantaRupKartari = createFixedRupResource(
  '/tinganta-rup-kartari',
  TINGANTA_RUP_KARTARI_FIELD_KEYS
);
export const tingantaRupKarmani = createFixedRupResource(
  '/tinganta-rup-karmani',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);
export const tingantaSanKartari = createFixedRupResource(
  '/tinganta-san-kartari',
  TINGANTA_RUP_KARTARI_FIELD_KEYS
);
export const tingantaSanKarmani = createFixedRupResource(
  '/tinganta-san-karmani',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);
export const tingantaNichKartari = createFixedRupResource(
  '/tinganta-nich-kartari',
  TINGANTA_RUP_KARTARI_FIELD_KEYS
);
export const tingantaNichKarmani = createFixedRupResource(
  '/tinganta-nich-karmani',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);
export const tingantaYangKartari = createFixedRupResource(
  '/tinganta-yang-kartari',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);
export const tingantaYangKarmani = createFixedRupResource(
  '/tinganta-yang-karmani',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);
export const tingantaYanglukKartari = createFixedRupResource(
  '/tinganta-yangluk-kartari',
  TINGANTA_RUP_PARASMAI_FIELD_KEYS
);
export const tingantaYanglukKarmani = createFixedRupResource(
  '/tinganta-yangluk-karmani',
  TINGANTA_RUP_KARMANI_FIELD_KEYS
);

/** --- Kṛt-style rows: arbitrary string keys besides id / tingantaId / timestamps. --- */

export type TingantaKrutRow = TingantaFixedRupRow;

function krutScalarEntries(row: Record<string, unknown>): [string, string | null][] {
  return Object.entries(row).filter(
    ([k, v]) => !TS_META.has(k) && (typeof v === 'string' || v == null)
  ) as [string, string | null][];
}

export type KrutRupResource = {
  basePath: string;
  list: (tingantaId?: number) => Promise<TingantaKrutRow[]>;
  get: (id: number) => Promise<TingantaKrutRow>;
  create: (body: { tingantaId: number } & Record<string, unknown>) => Promise<TingantaKrutRow>;
  update: (id: number, patch: Record<string, string | null>) => Promise<TingantaKrutRow>;
  remove: (id: number) => Promise<void>;
};

export function createKrutRupResource(basePath: string): KrutRupResource {
  const path = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return {
    basePath: path,
    async list(tingantaId?: number): Promise<TingantaKrutRow[]> {
      const res = await dictionaryApi.get<unknown>(path, {
        params: tingantaId != null ? { tingantaId } : undefined,
      });
      return normalizeList(res.data);
    },
    async get(id: number): Promise<TingantaKrutRow> {
      const res = await dictionaryApi.get<TingantaKrutRow>(`${path}/${id}`);
      return res.data;
    },
    async create(body: { tingantaId: number } & Record<string, unknown>): Promise<TingantaKrutRow> {
      const payload: Record<string, unknown> = { tingantaId: body.tingantaId };
      for (const [k, v] of Object.entries(body)) {
        if (k === 'tingantaId') continue;
        const n = normStr(v);
        if (n != null) payload[k] = n;
      }
      const res = await dictionaryApi.post<TingantaKrutRow>(path, payload);
      return res.data;
    },
    async update(id: number, patch: Record<string, string | null>): Promise<TingantaKrutRow> {
      const res = await dictionaryApi.patch<TingantaKrutRow>(`${path}/${id}`, patch);
      return res.data;
    },
    async remove(id: number): Promise<void> {
      await dictionaryApi.delete(`${path}/${id}`);
    },
  };
}

/** One `;`-separated group stored as four comma-separated fields: base, पुं, स्त्री, नपुं. */
export type KrutValueSegment = {
  base: string;
  pu: string;
  st: string;
  na: string;
};

export function parseKrutStoredValue(raw: string | null | undefined): KrutValueSegment[] {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return [emptyKrutSegment()];
  const chunks = s.split(';').map((c) => c.trim()).filter(Boolean);
  if (chunks.length === 0) return [emptyKrutSegment()];
  return chunks.map((chunk) => {
    const parts = chunk.split(',').map((p) => p.trim());
    while (parts.length < 4) parts.push('');
    return {
      base: parts[0] ?? '',
      pu: parts[1] ?? '',
      st: parts[2] ?? '',
      na: parts[3] ?? '',
    };
  });
}

function emptyKrutSegment(): KrutValueSegment {
  return { base: '', pu: '', st: '', na: '' };
}

/** Reverse of `parseKrutStoredValue`. */
export function serializeKrutStoredValue(segments: KrutValueSegment[] | undefined): string {
  const list = segments?.length ? segments : [emptyKrutSegment()];
  const joined = list
    .map((seg) => [seg.base, seg.pu, seg.st, seg.na].map((x) => (x == null ? '' : String(x).trim())).join(','))
    .join(';');
  if (!joined.replace(/[,;]/g, '').trim()) return '';
  return joined;
}

export function krutRowToKeyValues(row: TingantaKrutRow | null): { key: string; value: string }[] {
  if (!row) return [];
  return krutScalarEntries(row as Record<string, unknown>).map(([key, val]) => ({
    key,
    value: val == null ? '' : String(val),
  }));
}

export function krutRowToSegmentPairs(
  row: TingantaKrutRow | null
): { key: string; segments: KrutValueSegment[] }[] {
  return krutRowToKeyValues(row).map(({ key, value }) => ({
    key,
    segments: parseKrutStoredValue(value),
  }));
}

export function krutPairsFromSegmentForm(
  rows: { key?: string; segments?: KrutValueSegment[] }[] | undefined
): { key: string; value: string }[] {
  return (rows ?? [])
    .map((r) => ({
      key: (r.key ?? '').trim(),
      value: serializeKrutStoredValue(r.segments),
    }))
    .filter((r) => r.key !== '');
}

export function buildKrutPatch(prev: TingantaKrutRow, nextPairs: { key: string; value: string }[]) {
  const patch: Record<string, string | null> = {};
  const prevMap = new Map(
    krutScalarEntries(prev as Record<string, unknown>).map(([k, v]) => [
      k,
      normStr(v),
    ])
  );
  const nextMap = new Map<string, string | null>();
  for (const { key, value } of nextPairs) {
    const k = key.trim();
    if (!k) continue;
    nextMap.set(k, normStr(value));
  }
  const allKeys = new Set([...Array.from(prevMap.keys()), ...Array.from(nextMap.keys())]);
  Array.from(allKeys).forEach((k) => {
    const p = prevMap.get(k) ?? null;
    const n = nextMap.has(k) ? nextMap.get(k)! : null;
    if (p !== n) patch[k] = n;
  });
  return patch;
}

export function pairsFromKrutForm(rows: { key?: string; value?: string }[] | undefined) {
  return (rows ?? [])
    .map((r) => ({
      key: (r.key ?? '').trim(),
      value: r.value ?? '',
    }))
    .filter((r) => r.key !== '');
}

export const tingantaKrut = createKrutRupResource('/tinganta-krut');
export const tingantaSanKrut = createKrutRupResource('/tinganta-san-krut');
export const tingantaNichKrut = createKrutRupResource('/tinganta-nich-krut');
export const tingantaYangKrut = createKrutRupResource('/tinganta-yang-krut');
export const tingantaYanglukKrut = createKrutRupResource('/tinganta-yangluk-krut');
